import { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api.js';
import { toast } from '../../lib/toast.js';
import { waLink } from '../../lib/wa.js';
import { WaIcon } from '../primitives/WaIcon.js';

interface Cert {
  id: string;
  type: string;
  expiry: string | null;
  status: string;
  notes: string | null;
  docRef: string | null;
  docUrl?: string | null;
}
interface Worker { id: string; name: string; role: string; roles?: string[]; certs: Cert[]; }
interface Contact { id: string; name: string; email: string | null; phone: string | null; role: string | null; customerName?: string | null; }

interface Props { onClose: () => void; workerId: string; }

export function ShareTrainingModal({ onClose, workerId }: Props) {
  const [selected, setSelected] = useState<string[]>([]);
  const [extraEmail, setExtraEmail] = useState('');
  const [certChecks, setCertChecks] = useState<Record<string, boolean>>({});
  const [message, setMessage] = useState('');

  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const { data: w } = useQuery({ queryKey: ['workers', workerId], queryFn: () => api.get<Worker>(`/workers/${workerId}`).then((r) => r.data) });
  const { data: contacts = [] } = useQuery({ queryKey: ['contacts'], queryFn: () => api.get<Contact[]>('/contacts').then((r) => r.data) });

  const allCerts = useMemo(() => w?.certs ?? [], [w]);
  const hasDoc = (c: Cert) => !!(c.docRef || c.docUrl);

  // Default-tick every cert once worker loads — user can untick any they don't want shared.
  useEffect(() => {
    if (!w) return;
    const next: Record<string, boolean> = {};
    for (const c of allCerts) next[c.id] = true;
    setCertChecks(next);
  }, [w?.id, allCerts.length]);

  if (!w) return null;

  const toggle = (id: string) => setSelected((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);
  const toggleAll = (on: boolean) => setSelected(on ? contacts.map((c) => c.id) : []);
  const toggleCert = (id: string) => setCertChecks((p) => ({ ...p, [id]: !p[id] }));

  const rolesLine = (w.roles?.length ? w.roles : [w.role]).join(', ');
  const certCountLine = `${w.certs.length} certificate${w.certs.length !== 1 ? 's' : ''} on record`;

  const openReport = () => {
    const win = window.open(`/api/reports/training/worker/${workerId}/share.html`, '_blank');
    if (!win) { toast.warn('Blocked', 'Allow pop-ups for this site and try again'); return; }
    toast.success('Report Opened', 'Print dialog will open automatically');
  };

  const downloadReport = () => {
    const fname = `CLS_Training_${w.name.replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}.pdf`;
    window.open(`/api/reports/training/worker/${workerId}/share.pdf`, '_blank');
    toast.success('Downloaded', fname);
  };

  const sendWhatsApp = () => {
    const phoneContacts = contacts.filter((c) => selected.includes(c.id) && c.phone);
    if (phoneContacts.length === 0) {
      toast.warn('No recipients', 'Select contacts with phone numbers');
      return;
    }
    const rolesLineMsg = (w.roles?.length ? w.roles : [w.role]).join(', ');
    const tickedCerts = w.certs.filter((c) => certChecks[c.id]);
    const certLines = tickedCerts.length
      ? tickedCerts.map((c) => `- ${c.type} (${c.expiry?.slice(0, 10) ?? '—'}) — ${c.status === 'valid' ? 'Valid' : c.status === 'expiring' ? 'Expiring' : 'Expired'}`).join('\n')
      : 'None on record';
    const baseMsg = `Training Record — ${w.name}\nRole: ${rolesLineMsg}\n\nCertificates:\n${certLines}${message ? '\n\nNote: ' + message : ''}\n\n— CLS Construction Logistics Services`;

    phoneContacts.forEach((c, i) => {
      const text = `Hi ${c.name.split(' ')[0]},\n\n${baseMsg}`;
      setTimeout(() => window.open(waLink(c.phone, text), '_blank'), i * 600);
    });
    toast.success('WhatsApp', `Messages opened for ${phoneContacts.length} contact${phoneContacts.length !== 1 ? 's' : ''}`);
  };

  return createPortal(
    <div className="mo open" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 560 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-hdr" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <div className="modal-title" id="st-title">Share Training — {w.name}</div>
          <button className="modal-close close-btn" onClick={onClose}>✕</button>
        </div>

        <div id="st-worker-label" style={{ background: 'var(--info-dim)', border: '1px solid rgba(59,130,246,.2)', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 12 }}>
          <strong>{w.name}</strong> · {rolesLine}
          <br />
          <span style={{ fontSize: 10, color: 'var(--muted)' }}>{certCountLine}</span>
        </div>

        <div style={{ marginBottom: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
            <label className="form-label" style={{ margin: 0 }}>Recipients</label>
            <label style={{ fontSize: 11, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
              <input type="checkbox" id="st-all-contacts" checked={contacts.length > 0 && selected.length === contacts.length} onChange={(e) => toggleAll(e.target.checked)} style={{ accentColor: 'var(--accent)' }} /> Select all
            </label>
          </div>
          <div id="st-contacts-list" style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, padding: 6, maxHeight: 130, overflowY: 'auto' }}>
            {contacts.length === 0 ? (
              <div style={{ color: 'var(--muted)', fontSize: 11, padding: 8 }}>No contacts on record</div>
            ) : contacts.map((c) => (
              <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '5px 4px', borderBottom: '1px solid var(--border)', cursor: 'pointer', fontSize: 11 }}>
                <input type="checkbox" className="st-contact-cb" checked={selected.includes(c.id)} onChange={() => toggle(c.id)} style={{ accentColor: 'var(--accent)', flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 500 }}>{c.name}</div>
                  <div style={{ fontSize: 10, color: 'var(--muted)' }}>{c.role || '—'}{c.customerName ? ` · ${c.customerName}` : ''}</div>
                </div>
                <span style={{ fontSize: 10, color: 'var(--faint)', flexShrink: 0 }}>{c.email || 'no email'}</span>
              </label>
            ))}
          </div>
          <input className="form-input" id="st-extra-email" placeholder="Additional email address (optional)" value={extraEmail} onChange={(e) => setExtraEmail(e.target.value)} style={{ width: '100%', marginTop: 6 }} />
        </div>

        <div style={{ marginBottom: 10 }}>
          <label className="form-label">Certificates</label>
          <div id="st-cert-docs" style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, padding: 8, maxHeight: 180, overflowY: 'auto', fontSize: 11 }}>
            {allCerts.length === 0 ? (
              <div style={{ color: 'var(--muted)', fontSize: 11, padding: 4 }}>No certificates on record for this worker</div>
            ) : allCerts.map((c) => (
              <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '4px 2px', cursor: 'pointer', fontSize: 11 }}>
                <input type="checkbox" className="st-cert-cb" checked={!!certChecks[c.id]} onChange={() => toggleCert(c.id)} style={{ accentColor: 'var(--accent)', flexShrink: 0 }} />
                <span style={{ fontWeight: 500, flex: 1 }}>{c.type}</span>
                <span style={{ color: 'var(--muted)' }}>{c.expiry?.slice(0, 10) ?? '—'}</span>
                {hasDoc(c) ? (
                  <span style={{ fontSize: 10, color: 'var(--info)', marginLeft: 6 }}>📎 {c.docRef ?? c.docUrl?.split('/').pop() ?? 'attached'}</span>
                ) : null}
              </label>
            ))}
          </div>
          <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 3 }}>Ticked certificates appear in the WhatsApp message; ticked items with 📎 are also included as attachments in the report.</div>
        </div>

        <div style={{ marginBottom: 12 }}>
          <label className="form-label">Covering note (optional)</label>
          <textarea className="form-textarea" id="st-message" value={message} onChange={(e) => setMessage(e.target.value)} style={{ minHeight: 50, width: '100%' }} placeholder="Any context for the recipient…" />
        </div>

        <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginBottom: 8 }}>
          <button className="btn btn-primary" onClick={openReport} style={{ flex: 1, justifyContent: 'center' }}>🖨 Open &amp; Print Report</button>
          <button className="btn btn-ghost" onClick={downloadReport} style={{ flex: 1, justifyContent: 'center' }}>⬇ Download</button>
          <button className="btn btn-ghost" style={{ flex: 1, justifyContent: 'center', color: '#25D366', borderColor: '#25D366', display: 'inline-flex', alignItems: 'center', gap: 5 }} onClick={sendWhatsApp}><WaIcon size={13} /> WhatsApp</button>
        </div>

        <div style={{ fontSize: 10, color: 'var(--muted)', marginBottom: 12 }}>
          <strong>Open &amp; Print</strong> — opens a formatted printable report. Use Ctrl+P / ⌘P to print or save as PDF.{' '}
          <strong>Download</strong> — saves the report as an HTML file to attach to an email.{' '}
          <strong>WhatsApp</strong> — sends a text summary to selected contacts with phone numbers.
        </div>

        <div className="modal-actions">
          <button className="btn btn-ghost" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
